import React from 'react'
import { cn } from '@/lib/utils'
import { toneForStatus, type StatusKind } from '@/lib/phaseTone'

// ── MIDNIGHT BadgeColor ─────────────────────────────────────────────────────
export type BadgeColor =
  | 'neutral'
  | 'mint'
  | 'peach'
  | 'lavender'
  | 'butter'
  | 'blush'
  | 'sky'
  // @deprecated — use 'neutral' instead
  | 'slate'
  // @deprecated — use 'butter' instead
  | 'gold'
  // @deprecated — use 'blush' instead
  | 'red'
  // @deprecated — use 'mint' instead
  | 'moss'
  // @deprecated — use 'lime' instead (maps to mint)
  | 'lime'
  // @deprecated — use 'mint' instead (maps to mint)
  | 'green'
  // @deprecated — use 'butter' instead
  | 'yellow'
  // @deprecated — use 'butter' instead
  | 'honey'
  // @deprecated — use 'sky' instead
  | 'slateblue'
  // @deprecated — use 'lavender' instead
  | 'purple'
  // @deprecated — use 'sky' instead
  | 'blue'
  // @deprecated — use 'blush' instead
  | 'rose'
  // @deprecated — use 'peach' instead
  | 'orange'

interface BadgeProps {
  color?: BadgeColor
  children: React.ReactNode
  className?: string
  dot?: boolean
}

// Pastel chip recipe per MIDNIGHT brief:
// bg: rgba(base, 0.14), border: 0.5px solid rgba(base, 0.28), color: base hex
// Neutral: --midnight-tile bg + --midnight-border border + --midnight-text2 text

// Canonical pastel hex values (same as CSS tokens)
const PASTEL_HEX: Record<string, string> = {
  mint:     'var(--mint)',
  peach:    'var(--peach)',
  lavender: 'var(--lavender)',
  butter:   'var(--butter)',
  blush:    'var(--blush)',
  sky:      'var(--sky)',
}

// Deprecated alias map → canonical key
const ALIAS_MAP: Partial<Record<BadgeColor, string>> = {
  // @deprecated → neutral
  slate:     'neutral',
  // @deprecated → butter
  gold:      'butter',
  honey:     'butter',
  yellow:    'butter',
  // @deprecated → blush
  red:       'blush',
  rose:      'blush',
  // @deprecated → mint
  moss:      'mint',
  lime:      'mint',
  green:     'mint',
  // @deprecated → lavender
  purple:    'lavender',
  // @deprecated → sky
  slateblue: 'sky',
  blue:      'sky',
  // @deprecated → peach
  orange:    'peach',
}

function resolveColor(color: BadgeColor): string {
  return ALIAS_MAP[color] ?? color
}

function getChipStyle(color: BadgeColor): React.CSSProperties {
  const resolved = resolveColor(color)

  if (resolved === 'neutral') {
    return {
      background: 'var(--midnight-tile, rgb(var(--fg-rgb) / 0.04))',
      border: '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
      color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
    }
  }

  const hex = PASTEL_HEX[resolved]
  if (!hex) {
    // Fallback neutral if unknown
    return {
      background: 'var(--midnight-tile, rgb(var(--fg-rgb) / 0.04))',
      border: '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
      color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
    }
  }

  // Convert hex to rgb for rgba()
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  return {
    background:  `rgba(${r},${g},${b},0.14)`,
    border:      `0.5px solid rgba(${r},${g},${b},0.28)`,
    color:       hex,
  }
}

function getDotStyle(color: BadgeColor): React.CSSProperties {
  const resolved = resolveColor(color)
  if (resolved === 'neutral') {
    return { background: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))' }
  }
  const hex = PASTEL_HEX[resolved] ?? 'rgb(var(--fg-rgb) / 0.60)'
  return { background: hex }
}

export function Badge({ color = 'neutral', children, className, dot }: BadgeProps) {
  const chipStyle = getChipStyle(color)
  const dotStyle  = getDotStyle(color)

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold',
        className
      )}
      style={{
        height: 24,
        padding: '0 10px',
        fontSize: 12,
        letterSpacing: '0.01em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        ...chipStyle,
      }}
    >
      {dot && (
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={dotStyle}
        />
      )}
      {children}
    </span>
  )
}

// ── StatusBadge ─────────────────────────────────────────────────────────────
// Status strings supported — extended with both hyphen and underscore variants
type Status =
  | 'draft' | 'pending' | 'active' | 'completed' | 'overdue' | 'cancelled'
  | 'paid' | 'partial' | 'sent' | 'won' | 'lost' | 'scheduled'
  | 'in-progress' | 'in_progress'
  | 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'accepted' | 'rejected'
  | 'invoiced' | 'archived'
  // pipeline statuses from Work page
  | 'quoted'

// Work-stage colors come from the phase rule in lib/phaseTone (one pastel per
// phase), so a badge, a chip and a Work lane always agree. Only statuses with
// no phase (draft, cancelled, rejected…) set a color here.
const stage = (s: string, label: string) => ({ color: toneForStatus(s) as BadgeColor, label })

const statusMap: Record<Status, { color: BadgeColor; label: string }> = {
  draft:        { color: 'neutral',  label: 'Draft' },
  // Waiting is not a phase — neutral unless `kind` places it (a pending quote
  // is peach, a pending invoice butter; see StatusBadge below).
  pending:      { color: 'neutral',  label: 'Pending' },
  active:       stage('active', 'Active'),
  completed:    stage('completed', 'Completed'),
  overdue:      stage('overdue', 'Overdue'),
  cancelled:    { color: 'neutral',  label: 'Cancelled' },
  archived:     { color: 'neutral',  label: 'Archived' },
  paid:         stage('paid', 'Paid'),
  partial:      stage('partial', 'Partial'),
  sent:         { color: 'neutral',  label: 'Sent' },
  won:          stage('won', 'Won'),
  lost:         { color: 'neutral',  label: 'Lost' },
  scheduled:    stage('scheduled', 'Scheduled'),
  'in-progress':stage('in_progress', 'In progress'),
  in_progress:  stage('in_progress', 'In progress'),
  lead:         stage('lead', 'Lead'),
  qualified:    stage('qualified', 'Qualified'),
  quoted:       stage('quoted', 'Quoted'),
  proposal:     stage('proposal', 'Proposal'),
  negotiation:  stage('negotiation', 'Negotiation'),
  accepted:     stage('accepted', 'Accepted'),
  rejected:     { color: 'neutral',  label: 'Rejected' },
  invoiced:     stage('invoiced', 'Invoiced'),
}

interface StatusBadgeProps {
  status: Status | string
  /** The document the status sits on. Needed for statuses that mean different
   *  phases on different documents: a sent quote is peach, a sent invoice butter. */
  kind?: StatusKind
  className?: string
}

export function StatusBadge({ status, kind, className }: StatusBadgeProps) {
  // Callers pass both 'IN_PROGRESS' and 'in_progress'; the map is lowercase.
  const key = status.toLowerCase()
  const known = statusMap[key as Status]
  const kindTone = kind ? toneForStatus(key, kind) : 'neutral'
  const config = known
    ? { ...known, color: kindTone !== 'neutral' ? (kindTone as BadgeColor) : known.color }
    : { color: kindTone as BadgeColor, label: status }
  return (
    <Badge color={config.color} dot className={className}>
      {config.label}
    </Badge>
  )
}
